import type {
  PriceHistory,
  PriceHistoryRequestPrams,
  PriceHistoryResponse,
} from '@cronos-app/api';
import { getPriceHistory, PriceHistoryTimeframe } from '@cronos-app/api';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { LineData, Time } from 'lightweight-charts';

import { useGlobalChainLayer } from '../useGlobalChainLayer';

interface UsePriceHistoryParams extends UseQueryOptions<UsePriceHistoryParams> {
  token: PriceHistoryRequestPrams['token'];
  limit?: number;
  timeframe?: PriceHistoryTimeframe;
  beforeTimestamp?: number;
  inUsd?: boolean;
}

export const usePriceHistory = ({
  token,
  limit = 1000,
  timeframe = PriceHistoryTimeframe.MINUTE,
  beforeTimestamp,
  inUsd = true,
  ...options
}: UsePriceHistoryParams) => {
  const chainLayer = useGlobalChainLayer();

  return useQuery<LineData[]>(
    [
      usePriceHistory.queryKey,
      token,
      limit,
      beforeTimestamp,
      timeframe,
      inUsd,
      chainLayer,
    ],
    async () => {
      if (!token) throw new Error(`${token} should no be empty`);

      const priceHistoryResponse: PriceHistoryResponse = await getPriceHistory({
        chainLayer,
        beforeTimestamp,
        limit,
        timeframe,
        token,
      });

      return transformPriceHistoryResponse(
        priceHistoryResponse,
        inUsd ? 'price_usd' : 'price',
      );
    },
    {
      enabled: options.enabled === false ? false : !!token,
      refetchInterval: (data) => (data?.length ? false : 3000),
    },
  );
};
usePriceHistory.queryKey = 'usePriceHistory';

const transformPriceHistoryResponse = (
  priceHistoryResponse: PriceHistoryResponse,
  priceKey: keyof Pick<PriceHistory, 'price' | 'price_usd'>,
): LineData[] => {
  return priceHistoryResponse.map((item) => {
    return {
      value: item[priceKey],
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      time: Math.floor(new Date(item.time).getTime() / 1000) as Time,
    };
  });
};
